<template>
    <div id="settings-page">
        <loader v-if="is('loading')"></loader>
        <div class="container-fluid" v-if="is('ready')">
            <div class="row">
                <div class="col-md-12 col-sm-12">
                    <div class="box clearfix">
                        <div class="page-header">
                            <h1>{{ t('utmAuto-page_title') }}</h1>
                            <p class="page-description">
                                {{ t('utmAuto-page_description') }}
                            </p>
                        </div>

                        <span
                            class="alert"
                            v-bind:class="{
                                active: this.showNotification,
                                'alert-success': this.success,
                                'alert-warning': !this.success,
                            }"
                        >
                            <template v-if="success">
                                <span class="fa fa-check"></span> {{ t('utmAuto-success_message') }}
                            </template>
                            <template v-if="!success">
                                <span class="fa fa-remove"></span>
                                {{ errorMessage }}
                            </template>
                        </span>

                        <div class="settings-section">
                            <div class="toggle-section">
                                <label class="toggle-label">
                                    <input
                                        type="checkbox"
                                        v-model="enabled"
                                        class="toggle-checkbox"
                                    />
                                    <span class="toggle-text">
                                        <strong>{{ t('utmAuto-toggle-label') }}</strong>
                                        <small>{{ t('utmAuto-toggle-hint') }}</small>
                                    </span>
                                </label>
                            </div>

                            <div v-if="enabled" class="config-section">
                                <div class="config-card">
                                    <h3 class="card-title">
                                        <i class="fa fa-broadcast-tower"></i>
                                        {{ t('utmAuto-channels-title') }}
                                    </h3>
                                    <p class="card-description">
                                        {{ t('utmAuto-channels-description') }}
                                    </p>
                                    <div class="channels-grid">
                                        <label
                                            v-if="$module('email')"
                                            class="channel-option"
                                            :class="{ active: channels.includes('email') }"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="channels"
                                                value="email"
                                            />
                                            <div class="channel-content">
                                                <i class="fa fa-envelope"></i>
                                                <span>{{ t('utmAuto-channels-email') }}</span>
                                            </div>
                                        </label>
                                        <label
                                            v-if="$module('sms')"
                                            class="channel-option"
                                            :class="{ active: channels.includes('sms') }"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="channels"
                                                value="sms"
                                            />
                                            <div class="channel-content">
                                                <i class="fa fa-comment"></i>
                                                <span>{{ t('utmAuto-channels-sms') }}</span>
                                            </div>
                                        </label>
                                        <label
                                            v-if="$module('whatsapp')"
                                            class="channel-option"
                                            :class="{ active: channels.includes('whatsapp') }"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="channels"
                                                value="whatsapp"
                                            />
                                            <div class="channel-content">
                                                <i class="fa fa-whatsapp"></i>
                                                <span>{{ t('utmAuto-channels-whatsapp') }}</span>
                                            </div>
                                        </label>
                                        <label
                                            v-if="$module('whatsapp')"
                                            class="channel-option"
                                            :class="{ active: channels.includes('whatsapp-cloud') }"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="channels"
                                                value="whatsapp-cloud"
                                            />
                                            <div class="channel-content">
                                                <i class="fa fa-cloud"></i>
                                                <span>{{ t('utmAuto-channels-whatsapp_cloud') }}</span>
                                            </div>
                                        </label>
                                        <label
                                            v-if="$module('rcs')"
                                            class="channel-option"
                                            :class="{ active: channels.includes('rcs') }"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="channels"
                                                value="rcs"
                                            />
                                            <div class="channel-content">
                                                <span class="rcs-channel-icon ui-icon ui-message-notification-square"></span>
                                                <span>{{ t('utmAuto-channels-rcs') }}</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div class="config-card">
                                    <h3 class="card-title">
                                        <i class="fa fa-code"></i>
                                        {{ t('utmAuto-tokens-title') }}
                                    </h3>
                                    <p class="card-description">
                                        {{ t('utmAuto-tokens-description') }}
                                    </p>
                                    <div class="tokens-grid">
                                        <div
                                            class="token-item"
                                            v-for="(token, index) in utmTokens"
                                            :key="index"
                                            @click="copyToken(token)"
                                        >
                                            <code class="token-code">{{ token }}</code>
                                            <button class="token-copy-btn" :title="t('utmAuto-tokens-copy')">
                                                <i class="fa fa-copy"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div class="config-card">
                                    <h3 class="card-title">
                                        <i class="fa fa-sliders"></i>
                                        {{ t('utmAuto-params-title') }}
                                    </h3>
                                    <p class="card-description">
                                        {{ t('utmAuto-params-description') }}
                                    </p>

                                    <div class="utm-fields">
                                        <div class="utm-field">
                                            <label class="utm-label">
                                                <span class="utm-label-text">{{ t('utmAuto-params-source-label') }}</span>
                                                <small class="utm-label-hint">{{ t('utmAuto-params-source-hint') }}</small>
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control utm-input"
                                                v-model="utm_source"
                                                :placeholder="t('utmAuto-params-source-placeholder')"
                                            />
                                        </div>

                                        <div class="utm-field">
                                            <label class="utm-label">
                                                <span class="utm-label-text">{{ t('utmAuto-params-medium-label') }}</span>
                                                <small class="utm-label-hint">{{ t('utmAuto-params-medium-hint') }}</small>
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control utm-input"
                                                v-model="utm_medium"
                                                :placeholder="t('utmAuto-params-medium-placeholder')"
                                            />
                                        </div>

                                        <div class="utm-field">
                                            <label class="utm-label">
                                                <span class="utm-label-text">{{ t('utmAuto-params-campaign-label') }}</span>
                                                <small class="utm-label-hint">{{ t('utmAuto-params-campaign-hint') }}</small>
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control utm-input"
                                                v-model="utm_campaign"
                                                :placeholder="t('utmAuto-params-campaign-placeholder')"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="info-box">
                                    <i class="fa fa-info-circle"></i>
                                    <div class="info-content">
                                        <strong>{{ t('utmAuto-info-important') }}</strong> {{ t('utmAuto-info-description') }}
                                    </div>
                                </div>
                            </div>

                            <div class="actions-section">
                                <button
                                    class="btn btn-success btn-save"
                                    :disabled="!isValid || saving"
                                    @click.prevent="save"
                                >
                                    <span
                                        class="fa fa-spin fa-spinner"
                                        v-if="saving"
                                    ></span>
                                    <span v-else>
                                        <i class="fa fa-save"></i> {{ t('utmAuto-actions-save') }}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <input
                            id="tokenToCopy"
                            v-model="tokenToCopy"
                            type="text"
                            style="position: absolute; left: -9999px;"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
#settings-page {
    max-width: 1280px;
}
</style>
<script>
import keviApi from "../keviApi";

export default {
    data: function () {
        return {
            enabled: false,
            channels: [],
            utm_source: "",
            utm_medium: "",
            utm_campaign: "",
            saving: false,
            showNotification: false,
            state: "loading",
            success: true,
            errorMessage: "",
            tokenToCopy: null,
            utmTokens: [
                "[[nome_automacao]]",
                "[[id_acao]]",
                "[[data]]",
                "[[id_automacao]]",
                "[[canal]]"
            ],
        };
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        isValid() {
            if (!this.enabled) {
                return true;
            }
            return this.channels.length > 0;
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        getForm: function () {
            this.setState("loading");
            var options = keviApi.getOptions("settings/utm-auto");
            this.$http(options).then(
                (response) => {
                    this.setState("ready");
                    if (response.status === 200) {
                        this.enabled = response.body.enabled || false;
                        this.channels = response.body.channels || [];
                        this.utm_source = response.body.utm_source || "";
                        this.utm_medium = response.body.utm_medium || "";
                        this.utm_campaign = response.body.utm_campaign || "";
                    }
                },
                (error) => {
                    this.setState("ready");
                    // If endpoint doesn't exist yet, use defaults
                    this.enabled = false;
                    this.channels = [];
                    this.utm_source = "";
                    this.utm_medium = "";
                    this.utm_campaign = "";
                }
            );
        },
        save: function () {
            var options = keviApi.getOptions("settings/utm-auto", "POST", {
                enabled: this.enabled,
                channels: this.channels,
                utm_source: this.utm_source,
                utm_medium: this.utm_medium,
                utm_campaign: this.utm_campaign,
            });
            this.saving = true;
            this.showNotification = false;
            this.$http(options).then(
                (response) => {
                    this.saving = false;
                    if (response.status === 200) {
                        this.showNotification = true;
                        let _self = this;
                        window.setTimeout(function () {
                            _self.showNotification = false;
                        }, 5000);
                        if (response.body.status === "success") {
                            this.success = true;
                            this.getForm();
                            window.mixpanel?.track("Settings - save UTM auto", {
                                enabled: this.enabled,
                                channels: this.channels,
                                places_id: this.place.id,
                            });
                        } else {
                            this.success = false;
                            this.errorMessage = response.body.message || this.t('utmAuto-error_save');
                        }
                    }
                },
                (error) => {
                    this.saving = false;
                    this.success = false;
                    this.errorMessage = this.t('utmAuto-error_save_config');
                    this.showNotification = true;
                    let _self = this;
                    window.setTimeout(function () {
                        _self.showNotification = false;
                    }, 5000);
                }
            );
        },
        copyToken(token) {
            this.tokenToCopy = token;
            this.$nextTick(() => {
                let tokenToCopyInput = document.querySelector("#tokenToCopy");
                tokenToCopyInput.select();
                if (document.execCommand("copy")) {
                    this.$store.dispatch("notification/success", {
                        text: this.t('utmAuto-token_copied'),
                        duration: 5000,
                    });
                }
            });
        },
    },
    created: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t('utmAuto-browser_title');
        this.getForm();
    },
};
</script>
<style scoped>
.page-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
    margin: 0 0 8px 0;
    font-size: 28px;
    font-weight: 600;
    color: #111827;
}

.page-description {
    margin: 0;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.5;
}

.settings-section {
    margin-top: 20px;
}

.toggle-section {
    margin-bottom: 30px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.toggle-label {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    margin: 0;
}

.toggle-checkbox {
    margin-right: 12px;
    margin-top: 2px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    flex-shrink: 0;
    accent-color: #17B26A;
    -webkit-appearance: checkbox;
    appearance: checkbox;
}

.toggle-checkbox:checked {
    background-color: #17B26A;
    border-color: #17B26A;
}

.toggle-checkbox:focus {
    outline: 2px solid #17B26A;
    outline-offset: 2px;
}

.toggle-text {
    flex: 1;
}

.toggle-text strong {
    display: block;
    font-size: 16px;
    color: #111827;
    margin-bottom: 6px;
    font-weight: 600;
}

.toggle-text small {
    display: block;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.5;
}

.config-section {
    margin-top: 30px;
}

.config-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.card-title i {
    color: #17B26A;
    font-size: 16px;
}

.card-description {
    margin: 0 0 20px 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.5;
}

.channels-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
}

.channel-option {
    position: relative;
    cursor: pointer;
    margin: 0;
}

.channel-option input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.channel-option input[type="checkbox"]:checked + .channel-content {
    border-color: #17B26A;
    background: #F1FCF3;
}

.channel-option input[type="checkbox"]:checked + .channel-content i,
.channel-option input[type="checkbox"]:checked + .channel-content .rcs-channel-icon {
    color: #17B26A;
}

.channel-option input[type="checkbox"]:checked + .channel-content span {
    color: #00542E;
}

.channel-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.2s ease;
    text-align: center;
    gap: 8px;
}

.channel-content i,
.rcs-channel-icon {
    font-size: 24px;
    color: #9ca3af;
    transition: color 0.2s ease;
}

.rcs-channel-icon {
    width: 24px;
    height: 24px;
}

.channel-content span {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    transition: color 0.2s ease;
}

.channel-option:hover .channel-content {
    border-color: #17B26A;
    background: #F1FCF3;
}

.channel-option.active .channel-content {
    border-color: #17B26A;
    background: #F1FCF3;
}

.channel-option.active .channel-content i,
.channel-option.active .channel-content .rcs-channel-icon {
    color: #17B26A;
}

.channel-option.active .channel-content span {
    color: #00542E;
}

.tokens-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
}

.token-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    gap: 10px;
}

.token-item:hover {
    background: #f3f4f6;
    border-color: #17B26A;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(23, 178, 106, 0.1);
}

.token-code {
    flex: 1;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    color: #1f2937;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
}

.token-copy-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.token-copy-btn:hover {
    background: #e5e7eb;
    color: #17B26A;
}

.utm-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.utm-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.utm-label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
}

.utm-label-text {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
}

.utm-label-hint {
    font-size: 12px;
    color: #6b7280;
    font-weight: normal;
}

.utm-input {
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s ease;
}

.utm-input:focus {
    outline: none;
    border-color: #17B26A;
    box-shadow: 0 0 0 3px rgba(23, 178, 106, 0.1);
}

.info-box {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: #F1FCF3;
    border: 1px solid #A7CCC0;
    border-radius: 8px;
    margin-top: 20px;
}

.info-box i {
    color: #17B26A;
    font-size: 18px;
    margin-top: 2px;
    flex-shrink: 0;
}

.info-content {
    flex: 1;
    font-size: 13px;
    color: #00542E;
    line-height: 1.5;
}

.info-content strong {
    display: block;
    margin-bottom: 4px;
}

.actions-section {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
}

.btn-save {
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
}

.btn-save:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .channels-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .tokens-grid {
        grid-template-columns: 1fr;
    }
}
</style>

